import log from 'loglevel'
import { NodeSSH } from 'node-ssh'

import { deployFunction } from '../../configuration/interface'

// Runs pm2 start command and starts your build in pm2
const pm2Start = async (buildPath: string, pm2Env: string, appName: string, appPort: number, prependCommand: string, ssh: NodeSSH) => {
  log.info('running pm2Start')
  // TODO add to configuration
  const pm2ConfigFile = `ecosystem.${pm2Env}.config.js`

  // Stop and remove old running app process in pm2
  await pm2Del(appName, ssh)

  const startCommand = `${prependCommand}pm2 startOrRestart ${pm2ConfigFile} --env=${pm2Env} ${appName} ${appPort}`
  const startResult = await ssh.execCommand(
    startCommand,
    {
      cwd: buildPath,
    },
  )
  log.debug('Executed command over ssh:', startCommand, startResult)

  if (startResult.code && startResult.code !== 0) {
    log.error(startResult.stderr)
    throw new Error('SERVER START FAILED')
  }
  if (startResult.stderr) {
    log.warn(startResult.stderr)
  }

  log.info('finished pm2Start')
}

// Save current pm2 state, so it could be restored on server restart
const pm2Save = async (ssh: NodeSSH) => {
  log.info('running pm2Save')

  const command = 'pm2 save'
  const result = await ssh.execCommand(command)
  log.debug('Executed command over ssh:', command, result)

  if (result.code && result.code !== 0) {
    log.error(result.stderr)
    throw new Error('STARTUP SCRIPTS FAILED')
  }
  if (result.stderr) {
    log.warn(result.stderr)
  }

  log.info('finished pm2Save')
}

// Delete app from pm2
const pm2Del = async (appName: string, ssh: NodeSSH) => {
  log.info('running pm2Del')
  const delCommand = `pm2 delete -s ${appName}`
  const delResult = await ssh.execCommand(delCommand) // using -s (silent) to allow this to fail (fails when app is not running, this can happen when previous deployStart failed)
  log.debug('Executed command over ssh:', delCommand, delResult)
  log.info('finished pm2Del')
}

// Quote a value for safe use in a shell command (handles spaces and special characters)
const shellQuote = (value: string): string => `'${String(value).replace(/'/g, `'\\''`)}'`

// Build the `export VAR='value';` prefix for the pm2 start command
const buildEnvVarsPrepend = (envVars?: { [key: string]: string }, dynamicEnvVars?: { [key: string]: string }): string => {
  let prependCommand = ''
  if (envVars || dynamicEnvVars) {
    const allEnvVars = { ...envVars, ...dynamicEnvVars }
    Object.keys(allEnvVars).forEach(varName => {
      const varValue = allEnvVars[varName]
      prependCommand = `${prependCommand}export ${varName}=${shellQuote(varValue)};`
    })
  }
  return prependCommand
}

// Start build on server in pm2
export const pm2DeployStart: deployFunction = async (deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh) => {
  log.info('running deployStart')

  const { getAppName, getPort } = serverConfig
  if (!getPort) {
    throw new Error(`Missing getPort in server config for host "${serverConfig.sshConfig.host}". The default pm2 deployStart requires it.`)
  }
  const { envVars, environmentName } = environmentConfig
  const appName = await getAppName(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh)
  const port = await getPort(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh)
  const { buildPath, envVars: dynamicEnvVars } = dynamicArgs

  const prependCommand = buildEnvVarsPrepend(envVars, dynamicEnvVars)

  // Use pm2 to start build
  await pm2Start(buildPath, environmentName, appName, port, prependCommand, ssh)
  // Check if started build is running correctly
  await (environmentConfig.healthCheck || deployConfig.healthCheck)(ssh, port)
  // Call pm2 save so build would automatically start on server restart
  await pm2Save(ssh)

  log.info('finished deployStart')
}

// Setup some extra pm2 dynamic args
export const pm2DeployBefore: deployFunction = async (_deployConfig, _environmentConfig, _serverConfig, _dynamicArgs) => {
  log.info('running deployBefore')


  log.info('finished deployBefore')
}

// Clean older build on redeploy
export const pm2DeployAfter: deployFunction = async (deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh) => {
  log.info('running deployAfter')
  const { getAppName } = serverConfig
  const appName = await getAppName(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh)
  const { buildPath } = dynamicArgs
  const buildParentDir = buildPath.slice(0, buildPath.lastIndexOf("/"))
  // Get all folders of this build (environment + tag (preview only))
  const allBuildDirs = await getSubdirectories(buildParentDir, ssh)

  // Get active build folder
  const appCWD = await pm2GetAppCWD(appName, ssh)

  // Inactive build folders, newest first (folder names are timestamp-prefixed)
  const inactiveBuildDirs = allBuildDirs
    .map(buildDir => buildDir.replace(/\/$/, ''))
    .filter(buildDir => buildDir && !appCWD.includes(buildDir))
    .sort()
    .reverse()

  // Revertible environments keep the newest inactive build so the `revert`
  // cli command can switch back to it
  const deleteBuildDirs = environmentConfig.revertible ? inactiveBuildDirs.slice(1) : inactiveBuildDirs

  // Using for loop so all awaited promises must be resolved before next cycle
  for (let i = 0; i < deleteBuildDirs.length; i++) {
    await deleteFolder(`${buildParentDir}/${deleteBuildDirs[i]}`, ssh)
  }
  log.info('finished deployAfter')

}

export type RevertState = {
  buildParentDir: string,
  activeDir: string | undefined,
  targetDir: string | undefined,
}

// Inspect the server: where does the app run now and which previous build
// could it be reverted to (the newest inactive build folder)
export const getRevertState = async (uploadTo: string, appName: string, ssh: NodeSSH): Promise<RevertState> => {
  const buildParentDir = `${uploadTo}/${appName}`
  const allBuildDirs = await getSubdirectories(buildParentDir, ssh)
  const appCWD = await pm2GetAppCWD(appName, ssh)

  const buildDirs = allBuildDirs
    .map(buildDir => buildDir.replace(/\/$/, ''))
    .filter(Boolean)
  const activeDir = buildDirs.find(buildDir => appCWD.includes(buildDir))
  const inactiveDirs = buildDirs
    .filter(buildDir => buildDir !== activeDir)
    .sort()
    .reverse()

  return { buildParentDir, activeDir, targetDir: inactiveDirs[0] }
}

// Switch the app back to the previous build kept on the server
// (environments with revertible: true keep it after every deploy)
export const pm2Revert: deployFunction = async (deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh) => {
  log.info('running revert')

  const { getAppName, getPort } = serverConfig
  if (!getPort) {
    throw new Error(`Missing getPort in server config for host "${serverConfig.sshConfig.host}". The default pm2 revert requires it.`)
  }
  const { envVars, environmentName } = environmentConfig
  const appName = await getAppName(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh)
  const port = await getPort(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh)

  const { buildParentDir, activeDir, targetDir } = await getRevertState(environmentConfig.uploadConfig.uploadTo, appName, ssh)
  if (!targetDir) {
    throw new Error(`No previous build to revert to for app "${appName}" in ${buildParentDir}. Reverts need at least two deploys with revertible: true.`)
  }

  log.info(`Reverting app "${appName}" from version "${activeDir}" to version "${targetDir}"`)

  const prependCommand = buildEnvVarsPrepend(envVars, dynamicArgs.envVars)
  // Use pm2 to start the previous build (the ecosystem config file is part of every uploaded build)
  await pm2Start(`${buildParentDir}/${targetDir}`, environmentName, appName, port, prependCommand, ssh)
  // Check if the reverted build is running correctly
  await (environmentConfig.healthCheck || deployConfig.healthCheck)(ssh, port)
  // Call pm2 save so the reverted build survives a server restart
  await pm2Save(ssh)

  log.info('finished revert')
}


// Delete build folder from server on stop job
export const pm2OnStop: deployFunction = async (deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh) => {
  const { getAppName } = serverConfig
  const { buildPath } = dynamicArgs
  const appName = await getAppName(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh)
  const buildParentDir = buildPath.slice(0, buildPath.lastIndexOf("/"))
  // Stop and remove running app process in pm2
  await pm2Del(appName, ssh)
  // Save pm2 config
  await pm2Save(ssh)
  // Delete whole build folder
  await deleteFolder(buildParentDir, ssh)
}

const getSubdirectories = async (dir: string, ssh: NodeSSH): Promise<string[]> => {
  const command = `ls -1qd */`
  const result = await ssh.execCommand(command, {
    cwd: dir,
  })
  log.debug('Executed command over ssh:', command, result)

  if (result.code && result.code !== 0) {
    log.error(result.stderr)
    throw new Error(`ERROR GETTING SUB DIRS OF ${dir}`)
  }
  if (result.stderr) {
    log.warn(result.stderr)
  }

  return result.stdout.split('\n')
}

const deleteFolder = async (folder: string, ssh: NodeSSH): Promise<void> => {
  const command = `rm -r ${folder}`
  const result = await ssh.execCommand(command)
  log.debug('Executed command over ssh:', command, result)
  if (result.code && result.code !== 0) {
    log.error(result.stderr)
    throw new Error('ERROR DELETING FOLDER')
  }
  if (result.stderr) {
    log.warn(result.stderr)
  }
}


const pm2GetAppCWD = async (appName: string, ssh: NodeSSH): Promise<string> => {
  const command = `pm2 jlist`
  const result = await ssh.execCommand(command)
  log.debug('Executed command over ssh:', command, result)
  if (result.code && result.code !== 0) {
    log.error(result.stderr)
    throw new Error('ERROR GETTING PM2 PROCESSES')
  }
  if (result.stderr) {
    log.warn(result.stderr)
  }

  const processes = JSON.parse(result.stdout)
  const pm2Process = processes.find((pm2Process: { [key: string]: any }) => {
    return pm2Process.name === appName
  })
  if (!pm2Process) {
    throw new Error(`APP "${appName}" NOT FOUND IN PM2`)
  }

  return `${pm2Process.pm2_env.pm_cwd}/`
}
